import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ForgotPage } from './auth/ForgotPage';
import { LoginPage } from './auth/LoginPage';
import { PasswordPage } from './auth/PasswordPage';
import { afterLogin } from './auth/redirect';
import { RegisterPage } from './auth/RegisterPage';
import { useSession } from './auth/session';
import { BelegPage } from './beleg/BelegPage';
import { ClientApp } from './client/ClientApp';
import { AngabenPage } from './client/lohn/AngabenPage';
import { DokumentPage } from './client/lohn/DokumentPage';
import { t } from './i18n';
import { KanzleiApp } from './kanzlei/KanzleiApp';
import { RechnungTool } from './rechnung/RechnungTool';
import { useNativePush } from './lib/native';
import { useWebPush } from './lib/webpush';
import { isBilltoro } from './produkt';

// Billtoro erst bei Bedarf laden: Die Kanzlei-Instanz lädt nichts von src/billtoro außer dem, was das
// Rechnungswerkzeug selbst braucht (Kopfzeile, Hinweise)
const BilltoroApp = lazy(() => import('./billtoro/BilltoroApp'));

// Welche App: Billtoro (nur Rechnungswerkzeug, src/billtoro/BilltoroApp.tsx) oder Kanzlei wie bisher. Das Produkt steht
// für den ganzen Seitenaufruf fest (src/produkt.ts).
export function App() {
  return isBilltoro() ? (
    <Suspense fallback={<div className="spinner" role="status" aria-label="Lädt" />}>
      <BilltoroApp />
    </Suspense>
  ) : (
    <KanzleiInstanz />
  );
}

// Kanzlei-Instanz: Mandanten-App, Kanzlei und Rechnungswerkzeug
function KanzleiInstanz() {
  const { me, loading, signedOut } = useSession();
  const location = useLocation();
  useNativePush(me?.user.id ?? null);
  useWebPush(me?.user.id ?? null);
  if (loading) return <div className="spinner" aria-label={t('Lädt')} />;
  const home = me ? (me.user.kind === 'staff' ? '/kanzlei' : '/app') : '/login';
  // Ohne Anmeldung zur Anmeldung und die aufgerufene Seite merken; nach der Anmeldung geht es dorthin.
  // Nach „Abmelden“ nicht: Sonst landet der Nächste, der sich hier anmeldet, auf der letzten Seite des Vorgängers.
  const from = signedOut ? null : { from: location.pathname + location.search + location.hash };
  const toLogin = <Navigate to="/login" replace state={from} />;
  return (
    <Routes>
      <Route path="/login" element={me ? <Navigate to={afterLogin(location.state, me)} replace /> : <LoginPage />} />
      <Route path="/registrieren" element={me ? <Navigate to={home} replace /> : <RegisterPage />} />
      <Route path="/passwort-vergessen" element={<ForgotPage />} />
      <Route path="/passwort/:token" element={<PasswordPage />} />
      {/* Link für einen Mitarbeiter: ohne Anmeldung, auch wenn hier gerade jemand angemeldet ist */}
      <Route path="/angaben/:token" element={<AngabenPage />} />
      {/* Verdienstabrechnung oder Arbeitsvertrag für einen Mitarbeiter: Link aus der E-Mail, mit Geburtsdatum */}
      <Route path="/dokument/:token" element={<DokumentPage />} />
      {/* Rechnung, Mahnung oder Angebot für den Kunden eines Mandanten: Link aus der E-Mail, ohne Anmeldung */}
      <Route path="/beleg/:token" element={<BelegPage />} />
      <Route
        path="/app/*"
        element={!me ? toLogin : me.user.kind === 'client' ? <ClientApp me={me} /> : <Navigate to={home} replace />}
      />
      {/* Rechnungswerkzeug für Mandanten: eigene Seite, aus der App über „Zum Rechnungsmodul“ */}
      <Route
        path="/rechnung/*"
        element={!me ? toLogin : me.user.kind === 'client' ? <RechnungTool me={me} /> : <Navigate to={home} replace />}
      />
      <Route
        path="/kanzlei/*"
        element={!me ? toLogin : me.user.kind === 'staff' ? <KanzleiApp me={me} /> : <Navigate to={home} replace />}
      />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}
